import React, { useState, Component } from 'react';
import { Calendar as CalendarIcon, Clock, AlertCircle, CheckCircle, FileText, Users, TrendingUp, Download, ChevronLeft, ChevronRight } from 'lucide-react';
// --- Mock Data ---
const events = [{
  id: 1,
  title: "Lloyd's Syndicate Reporting Deadline",
  date: '2024-12-20',
  time: '17:00',
  category: 'compliance',
  status: 'upcoming',
  priority: 'high'
}, {
  id: 2,
  title: 'Marsh Ltd - Policy Renewal',
  date: '2024-12-22',
  time: '10:00',
  category: 'renewal',
  status: 'upcoming',
  priority: 'medium'
}, {
  id: 3,
  title: 'Quarterly Board Meeting',
  date: '2024-12-28',
  time: '14:00',
  category: 'meeting',
  status: 'upcoming',
  priority: 'high'
}, {
  id: 4,
  title: 'FCA Regulatory Return - RMAR',
  date: '2024-12-31',
  time: '23:59',
  category: 'compliance',
  status: 'upcoming',
  priority: 'critical'
}, {
  id: 5,
  title: 'Large Loss Review - CLM-2024-892',
  date: '2025-01-05',
  time: '11:00',
  category: 'claims',
  status: 'scheduled',
  priority: 'high'
}, {
  id: 6,
  title: 'Aon UK - Quarterly Review',
  date: '2025-01-08',
  time: '15:00',
  category: 'meeting',
  status: 'scheduled',
  priority: 'medium'
}, {
  id: 7,
  title: 'Solvency II Annual Return',
  date: '2025-01-15',
  time: '17:00',
  category: 'compliance',
  status: 'scheduled',
  priority: 'critical'
}, {
  id: 8,
  title: 'Reinsurance Treaty Renewal - Munich Re',
  date: '2025-01-20',
  time: '09:00',
  category: 'renewal',
  status: 'scheduled',
  priority: 'high'
}, {
  id: 9,
  title: 'Internal Audit - Claims Process',
  date: '2025-01-22',
  time: '10:00',
  category: 'compliance',
  status: 'scheduled',
  priority: 'medium'
}, {
  id: 10,
  title: 'Catastrophe Model Review',
  date: '2025-01-25',
  time: '14:00',
  category: 'meeting',
  status: 'scheduled',
  priority: 'medium'
}];
const categoryConfig = {
  compliance: {
    label: 'Compliance',
    color: 'bg-pink-50 text-pink-700 border-pink-200',
    icon: AlertCircle,
    dotColor: 'bg-pink-500'
  },
  renewal: {
    label: 'Policy Renewal',
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    icon: FileText,
    dotColor: 'bg-purple-500'
  },
  meeting: {
    label: 'Meeting',
    color: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    icon: Users,
    dotColor: 'bg-neutral-500'
  },
  claims: {
    label: 'Claims Review',
    color: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    icon: TrendingUp,
    dotColor: 'bg-neutral-400'
  }
};
const priorityConfig = {
  critical: {
    label: 'Critical',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50'
  },
  high: {
    label: 'High',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  medium: {
    label: 'Medium',
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-100'
  },
  low: {
    label: 'Low',
    color: 'text-neutral-500',
    bgColor: 'bg-neutral-50'
  }
};
// --- Helper Functions ---
const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};
const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1).getDay();
};
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });
};
const getEventsForDate = (dateString: string) => {
  return events.filter(event => event.date === dateString);
};
const isToday = (year: number, month: number, day: number) => {
  const today = new Date();
  return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
};
const getDaysUntil = (dateString: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(dateString);
  eventDate.setHours(0, 0, 0, 0);
  const diffTime = eventDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};
// --- Components ---
const EventCard = ({
  event
}: {
  event: any;
}) => {
  const category = categoryConfig[event.category as keyof typeof categoryConfig];
  const priority = priorityConfig[event.priority as keyof typeof priorityConfig];
  const CategoryIcon = category.icon;
  const daysUntil = getDaysUntil(event.date);
  return <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-986">
      <div className="flex items-start justify-between mb-3" data-id="element-987">
        <div className="flex items-start gap-3 flex-1" data-id="element-988">
          <div className={`p-2 rounded-lg ${category.color} border`} data-id="element-989">
            <CategoryIcon size={16} data-id="element-990" />
          </div>
          <div className="flex-1" data-id="element-991">
            <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-992">
              {event.title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-993">
              <span className="flex items-center gap-1" data-id="element-994">
                <CalendarIcon size={12} data-id="element-995" />
                {formatDate(event.date)}
              </span>
              <span className="flex items-center gap-1" data-id="element-996">
                <Clock size={12} data-id="element-997" />
                {event.time}
              </span>
            </div>
          </div>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${priority.bgColor} ${priority.color}`} data-id="element-998">
          {priority.label}
        </div>
      </div>
      <div className="flex items-center justify-between" data-id="element-999">
        <span className={`text-xs px-2 py-1 rounded-full ${category.color} border`} data-id="element-1000">
          {category.label}
        </span>
        {daysUntil >= 0 && <span className="text-xs text-gray-500" data-id="element-1001">
            {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
          </span>}
      </div>
    </div>;
};
export function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  const upcomingEvents = events.filter(e => getDaysUntil(e.date) >= 0).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 5);
  const thisWeekCount = events.filter(e => {
    const days = getDaysUntil(e.date);
    return days >= 0 && days <= 7;
  }).length;
  const overdueCount = events.filter(e => getDaysUntil(e.date) < 0).length;
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1002">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1003">
        <div data-id="element-1004">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1005">Calendar</h1>
          <p className="text-gray-500 mt-1" data-id="element-1006">
            Schedule and deadline management • FY 2024
          </p>
        </div>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm" data-id="element-1007">
          <Download size={16} className="mr-2" data-id="element-1008" />
          Export Schedule
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1009">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1010">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1011">
            This Week
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1012">{thisWeekCount}</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1013">Upcoming events</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1014">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1015">
            Total Events
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1016">{events.length}</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1017">Scheduled</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4 bg-red-50" data-id="element-1018">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1019">
            Overdue
          </p>
          <p className="text-2xl font-bold text-red-600" data-id="element-1020">{overdueCount}</p>
          <p className="text-xs text-red-700 mt-1" data-id="element-1021">Requires attention</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1022">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1" data-id="element-1023">
            Compliance
          </p>
          <p className="text-2xl font-bold text-gray-900" data-id="element-1024">
            {events.filter(e => e.category === 'compliance').length}
          </p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1025">Deadlines</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1026">
        {/* Calendar View */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1027">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6" data-id="element-1028">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1029">
              {monthNames[month]} {year}
            </h2>
            <div className="flex items-center gap-2" data-id="element-1030">
              <button onClick={previousMonth} className="p-2 hover:bg-gray-100 rounded-md transition-colors" data-id="element-1031">
                <ChevronLeft size={20} className="text-gray-600" data-id="element-1032" />
              </button>
              <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-1033">
                Today
              </button>
              <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-md transition-colors" data-id="element-1034">
                <ChevronRight size={20} className="text-gray-600" data-id="element-1035" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1" data-id="element-1036">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide py-2" data-id="element-1037">
                {day}
              </div>)}

            {/* Empty cells for days before month starts */}
            {Array.from({
            length: firstDay
          }).map((_, index) => <div key={`empty-${index}`} className="aspect-square" data-id="element-1038" />)}

            {/* Calendar Days */}
            {Array.from({
            length: daysInMonth
          }).map((_, index) => {
            const day = index + 1;
            const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = getEventsForDate(dateString);
            const today = isToday(year, month, day);
            return <button key={day} onClick={() => setSelectedDate(dateString)} className={`aspect-square p-2 rounded-lg border transition-all ${today ? 'bg-blue-50 border-blue-500 font-semibold' : selectedDate === dateString ? 'bg-blue-100 border-blue-300' : dayEvents.length > 0 ? 'bg-gray-50 border-gray-200 hover:bg-gray-100' : 'border-gray-100 hover:bg-gray-50'}`} data-id="element-1039">
                  <div className="flex flex-col items-center justify-center h-full" data-id="element-1040">
                    <span className={`text-sm ${today ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-1041">
                      {day}
                    </span>
                    {dayEvents.length > 0 && <div className="flex gap-0.5 mt-1" data-id="element-1042">
                        {dayEvents.slice(0, 3).map((event, idx) => {
                    const category = categoryConfig[event.category as keyof typeof categoryConfig];
                    return <div key={idx} className={`w-1 h-1 rounded-full ${category.dotColor}`} data-id="element-1043" />;
                  })}
                      </div>}
                  </div>
                </button>;
          })}
          </div>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap gap-4 text-xs" data-id="element-1044">
            {Object.entries(categoryConfig).map(([key, config]) => <div key={key} className="flex items-center gap-2" data-id="element-1045">
                <div className={`w-3 h-3 rounded-full ${config.dotColor}`} data-id="element-1046" />
                <span className="text-gray-600" data-id="element-1047">{config.label}</span>
              </div>)}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1048">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1049">
            Upcoming Events
          </h2>
          <div className="space-y-3" data-id="element-1050">
            {upcomingEvents.map(event => <EventCard key={event.id} event={event} data-id="element-1051" />)}
          </div>
          {upcomingEvents.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-1052">
              <CheckCircle size={48} className="mx-auto mb-3 text-gray-300" data-id="element-1053" />
              <p className="text-sm" data-id="element-1054">No upcoming events</p>
            </div>}
        </div>
      </div>
    </div>;
}